import React, { useState, useEffect } from "react";
import { Icon } from "../icons.jsx";
import {
  Card, Button, Badge, Field, Check, Radio, Switch, Segmented, Modal,
  Donut, MultiDonut, BarChart, Slider,
} from "../components.jsx";
import { useStore } from "../store.jsx";
import { TALENTION_HELPERS } from "../data.js";

import { PageHead } from "./dashboard.jsx";

/* ============================================================
   Talention — Factores screen
   4 grupos, SVPT siempre = 100%, items con peso y selección
   ============================================================ */

function Factors() {
  const { state, dispatch } = useStore();
  const [expanded, setExpanded] = useState(null);
  const [autoBalance, setAutoBalance] = useState(false);

  const groups = state.factors.groups;
  const totalSVPT = groups.reduce((s, g) => s + g.svpt, 0);
  const balanced = totalSVPT === 100;
  const diff = totalSVPT - 100;

  const setSVPT = (groupId, value) => {
    if (autoBalance) {
      // adjust the other groups proportionally
      const remaining = 100 - value;
      const others = groups.filter(g => g.id !== groupId);
      const otherSum = others.reduce((s, g) => s + g.svpt, 0) || 1;
      others.forEach(g => {
        const ratio = g.svpt / otherSum;
        const newV = Math.round(remaining * ratio);
        dispatch({ type: "SET_FACTOR_SVPT", groupId: g.id, value: Math.max(0, newV) });
      });
      dispatch({ type: "SET_FACTOR_SVPT", groupId, value });
    } else {
      dispatch({ type: "SET_FACTOR_SVPT", groupId, value });
    }
  };

  return (
    <div className="stack" style={{ gap: 22 }}>
      <PageHead
        title="Factores de Valoración"
        sub="Define la importancia relativa de los cuatro grupos de factores. La suma de los pesos SVPT debe ser exactamente 100%."
        actions={
          <div className="row">
            <Button variant="ghost" icon="x" onClick={() => dispatch({ type: "RESET_FACTORS" })}>Reset</Button>
            <Button variant="secondary" onClick={() => dispatch({ type: "AUTOBALANCE_FACTORS" })} icon="sparkle">
              Reajustar a 100
            </Button>
          </div>
        }
      />

      {/* Summary panel */}
      <Card style={{ overflow: "hidden", position: "relative" }}>
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 28, alignItems: "center" }}>
          <MultiDonut
            size={180}
            stroke={20}
            segments={groups.map(g => ({ value: g.svpt, color: g.color }))}
          >
            <div>
              <div className="display-num" style={{ fontSize: 38, color: balanced ? "var(--ink-900)" : "var(--bad-500)" }}>
                {totalSVPT}<span style={{ fontSize: 22 }}>%</span>
              </div>
              <div className="tiny muted">{balanced ? "Equilibrado" : diff > 0 ? `${diff}% por encima` : `${-diff}% por debajo`}</div>
            </div>
          </MultiDonut>

          <div className="stack" style={{ gap: 10 }}>
            <div>
              <div className="card-eyebrow">Distribución actual de pesos</div>
              <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4, letterSpacing: "-0.02em" }}>
                {balanced ? "Pesos correctamente equilibrados al 100%" : "Ajusta los pesos para sumar 100%"}
              </div>
            </div>
            <div className="grid grid-4" style={{ gap: 10 }}>
              {groups.map(g => (
                <div key={g.id} style={{
                  padding: "10px 12px", background: "var(--bg-soft)", borderRadius: 10,
                  borderLeft: `3px solid ${g.color}`,
                }}>
                  <div className="tiny muted">{g.short}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em" }}>
                    {g.svpt}<span className="muted" style={{ fontSize: 13, fontWeight: 500 }}>%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="stack" style={{ gap: 8, alignItems: "stretch", minWidth: 160 }}>
            <div className="card-eyebrow">Auto-balance</div>
            <div className="row" style={{ gap: 8 }}>
              <Switch on={autoBalance} onChange={setAutoBalance} />
              <span className="tiny muted">Reajustar al mover</span>
            </div>
          </div>
        </div>

        {!balanced && (
          <div className={"banner " + (Math.abs(diff) > 15 ? "bad" : "warn")} style={{ marginTop: 18 }}>
            <Icon name="alert" size={18} className="banner-icon" />
            <div style={{ flex: 1 }}>
              <div className="semi" style={{ fontSize: 13 }}>
                {diff > 0
                  ? `Los pesos suman ${totalSVPT}%, te has pasado ${diff} puntos`
                  : `Los pesos suman ${totalSVPT}%, faltan ${-diff} puntos para llegar al 100%`}
              </div>
              <div className="tiny" style={{ marginTop: 2, opacity: 0.85 }}>
                Las valoraciones de los puestos no son fiables hasta que los pesos sumen exactamente 100%.
              </div>
            </div>
            <button onClick={() => dispatch({ type: "AUTOBALANCE_FACTORS" })}
              style={{
                border: "none", background: "white", padding: "7px 12px",
                borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}>
              Reajustar automáticamente
            </button>
          </div>
        )}
      </Card>

      {/* 4 factor cards */}
      <div className="grid grid-2">
        {groups.map(group => (
          <FactorCard
            key={group.id}
            group={group}
            items={state.factors.items[group.id]}
            expanded={expanded === group.id}
            onToggle={() => setExpanded(expanded === group.id ? null : group.id)}
            onSVPT={(v) => setSVPT(group.id, v)}
            onToggleItem={(itemId) => dispatch({ type: "TOGGLE_FACTOR_ITEM", groupId: group.id, itemId })}
            onWeight={(itemId, value) => dispatch({ type: "SET_ITEM_WEIGHT", groupId: group.id, itemId, value })}
            onEqualize={() => dispatch({ type: "EQUALIZE_ITEM_WEIGHTS", groupId: group.id })}
          />
        ))}
      </div>

      <div className="banner info">
        <Icon name="info" size={18} className="banner-icon" />
        <div>
          <div className="semi" style={{ fontSize: 13, marginBottom: 2 }}>¿Cómo funcionan los pesos?</div>
          <div className="tiny" style={{ lineHeight: 1.5 }}>
            El <b>SVPT (Sub-Valor Ponderado Total)</b> es el peso relativo de cada grupo en el cálculo final. Dentro de cada grupo, los <b>pesos individuales</b> de los factores seleccionados se reparten proporcionalmente. Los grupos suman 100% entre todos.
          </div>
        </div>
      </div>
    </div>
  );
}

function FactorCard({ group, items, expanded, onToggle, onSVPT, onToggleItem, onWeight, onEqualize }) {
  const selectedCount = items.filter(i => i.selected).length;
  const itemWeightSum = items.filter(i => i.selected && i.levels.length > 0).reduce((s, i) => s + (i.weight || 0), 0);

  return (
    <Card className={"factor-card " + group.tint} style={{ padding: 0, overflow: "hidden", "--tint": group.color }}>
      <div className="factor-accent" style={{ background: group.color }} />
      <div style={{ padding: 22 }}>
        <div className="row" style={{ gap: 12, marginBottom: 16 }}>
          <div className="factor-emoji" style={{ background: group.iconBg, color: group.color }}>
            <Icon name={group.icon} size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.015em" }}>{group.name}</div>
            <div className="tiny muted" style={{ marginTop: 2, textWrap: "pretty" }}>{group.description}</div>
          </div>
        </div>

        {/* SVPT slider */}
        <div style={{
          padding: 16, borderRadius: 14, background: "var(--bg-soft)",
        }}>
          <div className="spread" style={{ marginBottom: 8 }}>
            <span className="card-eyebrow">SVPT</span>
            <span style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em" }}>
              {group.svpt}<span className="muted" style={{ fontSize: 14, fontWeight: 500 }}>%</span>
            </span>
          </div>
          <Slider value={group.svpt} onChange={onSVPT} min={0} max={100} color={group.color} />
        </div>

        {/* Item summary */}
        <div className="spread" style={{ marginTop: 16 }}>
          <div className="row" style={{ gap: 8 }}>
            <Badge style={{ background: group.color + "1f", color: group.color }}>{selectedCount} de {items.length} seleccionados</Badge>
          </div>
          <div className="row" style={{ gap: 6 }}>
            {selectedCount > 0 && (
              <button onClick={onEqualize} className="btn btn-ghost btn-sm" style={{ gap: 4 }}>
                <Icon name="sparkle" size={12} />
                Igualar
              </button>
            )}
            <button onClick={onToggle} className="btn btn-ghost btn-sm" style={{ gap: 4 }}>
              <Icon name={expanded ? "chevron-down" : "chevron-right"} size={14} />
              {expanded ? "Ocultar" : "Ver factores"}
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="fade-in" style={{ borderTop: "1px solid var(--border)", background: "var(--bg-soft)" }}>
          <div style={{ padding: "8px 0" }}>
            {items.map(it => (
              <FactorItemRow
                key={it.id}
                item={it}
                tint={group.color}
                groupSvpt={group.svpt}
                itemWeightSum={itemWeightSum}
                onToggle={() => onToggleItem(it.id)}
                onWeight={(v) => onWeight(it.id, v)}
              />
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function FactorItemRow({ item, tint, groupSvpt, itemWeightSum, onToggle, onWeight }) {
  const w = item.weight || 0;
  const effectivePct = itemWeightSum > 0 ? Math.round((w / itemWeightSum) * groupSvpt) : 0;

  return (
    <div style={{
      padding: "10px 22px",
      borderBottom: "1px solid var(--border)",
      background: item.selected ? "white" : "transparent",
      transition: "background 120ms",
    }}>
      <div className="row" style={{ gap: 14 }}>
        <Check checked={item.selected} onChange={onToggle} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ gap: 8 }}>
            <span className="semi" style={{ fontSize: 13.5, color: item.selected ? "var(--ink-900)" : "var(--ink-500)" }}>{item.name}</span>
            {item.gender !== "NEUTRO" && (
              <span style={{
                fontSize: 9.5, padding: "1px 6px", borderRadius: 999, fontWeight: 700,
                letterSpacing: 0.04 + "em",
                background: item.gender === "MASCULINO" ? "#dfeeff" : "#fde2e8",
                color: item.gender === "MASCULINO" ? "#0a52c2" : "#a01755",
              }}>{item.gender}</span>
            )}
            {item.levels && item.levels.length > 0 && <span className="tiny muted">· {item.levels.length} niveles</span>}
          </div>
          {item.desc && item.selected && (
            <div className="tiny muted" style={{ marginTop: 2 }}>{item.desc}</div>
          )}
        </div>
        {item.selected && item.levels.length > 0 && (
          <div className="row" style={{ gap: 6, alignItems: "center" }}>
            <button
              onClick={() => onWeight(Math.max(0, w - 1))}
              style={{
                width: 26, height: 26, border: "1px solid var(--border)", borderRadius: 6,
                background: "var(--bg-soft)", cursor: "pointer", fontWeight: 700, fontSize: 15,
                display: "grid", placeItems: "center", lineHeight: 1,
              }}>−</button>
            <input
              type="number"
              min={0}
              value={w}
              onChange={e => onWeight(Math.max(0, parseInt(e.target.value) || 0))}
              style={{
                width: 52, textAlign: "center", border: "1px solid var(--border)", borderRadius: 6,
                padding: "4px 4px", fontSize: 13, fontWeight: 600, outline: "none",
                background: "white",
              }}
            />
            <button
              onClick={() => onWeight(w + 1)}
              style={{
                width: 26, height: 26, border: "1px solid var(--border)", borderRadius: 6,
                background: "var(--bg-soft)", cursor: "pointer", fontWeight: 700, fontSize: 15,
                display: "grid", placeItems: "center", lineHeight: 1,
              }}>+</button>
            <span style={{
              fontSize: 11.5, fontWeight: 600, minWidth: 38, textAlign: "right",
              color: effectivePct > 0 ? tint : "var(--ink-400)",
            }}>{effectivePct}%</span>
          </div>
        )}
      </div>
    </div>
  );
}

export { Factors };